import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';

/**
 * Stateful Button（Aceternity UI 风格）— 点击后展示加载 → 成功/失败状态
 */
export function StatefulButton({
  children,
  onAction,
  className = '',
  successText = '完成',
  errorText = '失败',
  disabled,
}: {
  children: React.ReactNode;
  onAction: () => Promise<void>;
  className?: string;
  successText?: string;
  errorText?: string;
  disabled?: boolean;
}) {
  const [state, setState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const run = async () => {
    if (state === 'loading' || disabled) return;
    setState('loading');
    try {
      await onAction();
      setState('success');
      setTimeout(() => setState('idle'), 1600);
    } catch {
      setState('error');
      setTimeout(() => setState('idle'), 1800);
    }
  };

  return (
    <motion.button
      type="button"
      onClick={run}
      disabled={disabled || state === 'loading'}
      whileHover={state === 'idle' ? { scale: 1.02 } : {}}
      whileTap={state === 'idle' ? { scale: 0.97 } : {}}
      className={`relative flex items-center justify-center gap-2 overflow-hidden rounded-xl font-bold ${className}`}
    >
      <AnimatePresence mode="wait" initial={false}>
        {state === 'idle' && (
          <motion.span key="idle" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.15 }} className="flex items-center gap-2">
            {children}
          </motion.span>
        )}
        {state === 'loading' && (
          <motion.span key="loading" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.15 }} className="flex items-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin" /> 处理中...
          </motion.span>
        )}
        {state === 'success' && (
          <motion.span key="success" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ type: 'spring', stiffness: 400, damping: 22 }} className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5" /> {successText}
          </motion.span>
        )}
        {state === 'error' && (
          <motion.span key="error" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="flex items-center gap-2">
            <XCircle className="w-5 h-5" /> {errorText}
          </motion.span>
        )}
      </AnimatePresence>
    </motion.button>
  );
}
